/* eslint-disable default-case-last */
/* eslint-disable no-mixed-operators */
import { createSelector } from 'reselect'
import moment from 'moment'
import { toHslaString } from '@imodules/react-components/forms/shared'

import { getTimes, getSeries, getGraphOptionDefaults } from './mappers'

export const getDashboardConfiguration = (state: RootState) => state.dashboard

export const getDashboardInstanceSettings = createSelector(getDashboardConfiguration, dashboardConfiguration => dashboardConfiguration && dashboardConfiguration.instanceSettings)

export const getDashboardData = createSelector(getDashboardConfiguration, dashboardState => dashboardState && dashboardState.data)

export const getDollarData = createSelector(getDashboardData, data => data && data.dollarData)

export const getDonorData = createSelector(getDashboardData, data => data && data.donorData)

export const getChartStyle = createSelector(getDashboardConfiguration, config => config && config.chartStyle || 'bar')
// tslint:disable-next-line:restrict-plus-operands
export const getTotalDollars = createSelector(getDonorData, data => data && data.reduce<number>((a, i) => a + i.totalDollars, 0))
// tslint:disable-next-line:restrict-plus-operands
export const getTotalDonors = createSelector(getDonorData, data => data && data.reduce<number>((a, i) => a + i.totalDonations, 0))

const getAppeals = (data: CampaignDashboardDataRecord[]) => [...new Set(data.map(i => i.appealCode))].map(i => ({ name: i || 'No Appeal' }))

export const getTitleTextStyle = createSelector(getDashboardInstanceSettings, instanceSettings => instanceSettings && {
    textAlign: 'center',
    fontSize: `${instanceSettings.layoutSettings.titleFontSize}px`,
    color: toHslaString(instanceSettings.layoutSettings.titleColor),
    fontStyle: instanceSettings.layoutSettings.titleItalic ? 'italic' : 'normal',
    fontWeight: instanceSettings.layoutSettings.titleBold ? 'bold' : 'normal',
})

export const getSubTitleTextStyle = createSelector(getDashboardInstanceSettings, instanceSettings => instanceSettings && {
    textAlign: 'center',
    fontSize: `${instanceSettings.layoutSettings.subTitleFontSize}px`,
    color: toHslaString(instanceSettings.layoutSettings.subTitleColor),
    fontStyle: instanceSettings.layoutSettings.subTitleItalic ? 'italic' : 'normal',
    fontWeight: instanceSettings.layoutSettings.subTitleBold ? 'bold' : 'normal',
})

export const getCampaignTextStyle = createSelector(getDashboardInstanceSettings, instanceSettings => instanceSettings && {
    textAlign: 'center',
    color: toHslaString(instanceSettings.layoutSettings.campaignFontColor),
    backgroundColor: toHslaString(instanceSettings.layoutSettings.campaignBoxColor),
})

export const getDollarsTextStyle = createSelector(getDashboardInstanceSettings, instanceSettings => instanceSettings && {
    textAlign: 'center',
    color: toHslaString(instanceSettings.layoutSettings.dollarsFontColor),
    backgroundColor: toHslaString(instanceSettings.layoutSettings.dollarsBoxColor),
})

export const getContributionsTextStyle = createSelector(getDashboardInstanceSettings, instanceSettings => instanceSettings && {
    textAlign: 'center',
    color: toHslaString(instanceSettings.layoutSettings.contributionsFontColor),
    backgroundColor: toHslaString(instanceSettings.layoutSettings.contributionsBoxColor),
})

export const getGraphType = createSelector(getDashboardConfiguration, dashboardConfig => dashboardConfig && dashboardConfig.graph)

export const getLegendValues = createSelector(getDashboardConfiguration, state => state && state.selected)

const getLegendData = (data: ReturnType<typeof getDonorData>, selected: DashboardState['selected']) => ({ data: data ? getAppeals(data) : [], selected })

export const getDonorLegend = createSelector(getDonorData, getLegendValues, getLegendData)
export const getDollarLegend = createSelector(getDollarData, getLegendValues, getLegendData)

export const formatter = (interval: CampaignDashboardDataContainer['interval'] | undefined) => (value: string | moment.Moment) => {
    let format: string
    switch (interval) {
        case 'Year': {
            format = 'YYYY'
            break
        }
        case 'Month': {
            format = 'MMM [\']YY'
            break
        }
        case 'Day':
        case 'Week': {
            format = 'MMM Do'
            break
        }
        default:
        case 'Hour': {
            format = 'h a'
            break
        }
    }
    return moment(value).format(format)
}
export const getInterval = createSelector(getDashboardData, dashboardData => dashboardData && dashboardData.interval)
export const getDonorXAxis = createSelector(getDonorData, getInterval, (donorData, interval) => ([
{
    type: 'category',
    data: donorData && getTimes(donorData),
    axisTick: {
        alignWithLabel: true,
    },
    axisLabel: {
        formatter: formatter(interval),
    },
}
]))

export const getDollarXAxis = createSelector(getDollarData, getInterval, (dollarData, interval) => ([
{
    type: 'category',
    data: dollarData && getTimes(dollarData),
    axisTick: {
        alignWithLabel: true,
    },
    axisLabel: {
        formatter: formatter(interval),
    },
}
]))

export const getDonorGraphOptions = createSelector(getChartStyle, getDonorData, getDonorLegend, getDonorXAxis, getDashboardInstanceSettings, (chartStyle, donorData, legend, donorXAxis, instanceSettings) => ({
    ...getGraphOptionDefaults(instanceSettings),
    legend,
    series: getSeries(donorData, true, chartStyle),
    xAxis: donorXAxis,
    yAxis: [
        {
            type: 'value',
            axisLabel: {
                formatter: '{value}',
            },
            name: 'Donors',
        },
    ],
}))

export const getDollarGraphOptions = createSelector(getChartStyle, getDollarData, getDollarLegend, getDollarXAxis, getDashboardInstanceSettings, (chartStyle, dollarData, legend, dollarXAxis, instanceSettings) => ({
    ...getGraphOptionDefaults(instanceSettings),
    legend,
    series: getSeries(dollarData, false, chartStyle),
    xAxis: dollarXAxis,
    yAxis: [
        {
            type: 'value',
            axisLabel: {
                formatter: '$ {value}',
            },
            name: 'Dollars',
        },
    ],
}))

export const getDashboardDisplayEndDateTime = createSelector(getDashboardInstanceSettings, settings => settings && settings.displayEndDateTime && moment(settings.displayEndDateTime))

export const getDashboardDisplayStartDateTime = createSelector(getDashboardInstanceSettings, settings => settings && moment(settings.displayStartDateTime))

export const getShowModule = createSelector(getDashboardDisplayEndDateTime, getDashboardDisplayStartDateTime, (displayEndDateTime, displayStartDateTime) =>
    moment().isAfter(displayStartDateTime) && (!displayEndDateTime || moment().isBefore(displayEndDateTime)))
